import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { imagesApi, searchApi } from '../api';
import {
  Button,
  EmptyState,
  ErrorState,
  Loading,
  Select,
  Tabs,
  TextInput,
} from '../components/common';
import { AppShell } from '../components/layout/AppShell';
import { Sidebar } from '../components/layout/Sidebar';
import { TopBar } from '../components/layout/TopBar';
import { AnalysisResults, ImageResults, ManualResults } from '../components/search';
import { useModel } from '../context/ModelContext';
import { useToast } from '../context/ToastContext';
import { useConversations } from '../hooks/useConversations';
import { useImageUpload } from '../hooks/useImageUpload';
import { useSearch, useSearchStatus } from '../hooks/useSearch';
import '../components/search/search.css';
import './pages.css';

/** The image model's text tower truncates past this, silently. */
const TEXT_TOWER_TOKENS = 64;

const MODES = [
  { id: 'similar', label: 'Similar images', collection: 'survey_images' },
  { id: 'describe', label: 'Describe a scene', collection: 'survey_images' },
  { id: 'codings', label: 'Codings', collection: 'analyses' },
  { id: 'manual', label: 'Coding manual', collection: 'manual_chunks' },
];

/**
 * Similarity search over the survey images, the codings and the coding manual.
 *
 * Four modes on one page rather than four pages: they share every control around
 * the query, and the results all answer "what else is like this". They are
 * separate tabs rather than a dropdown because the *input* differs — an image, a
 * phrase, a phrase with filters — and a control that changes shape under a
 * dropdown is harder to follow than four labelled tabs.
 *
 * The page is careful about one distinction the API is also careful about: an
 * empty result and an unavailable store are different answers. Search off,
 * nothing indexed, and nothing similar each get their own state, because the
 * useful next action is different in all three and no user should have to guess
 * which one they are looking at.
 */
export function SearchPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { readyVersions } = useModel();
  const conversations = useConversations();
  const status = useSearchStatus();

  const [mode, setMode] = useState('similar');
  const [text, setText] = useState('');
  const [limit, setLimit] = useState(20);
  const [versionId, setVersionId] = useState('');
  const [answerMode, setAnswerMode] = useState('');
  const [attribute, setAttribute] = useState('');
  const [seed, setSeed] = useState(null);
  const [dropping, setDropping] = useState(false);

  const upload = useImageUpload({ max: 1 });

  const runner = useMemo(
    () =>
      ({
        similar: searchApi.images,
        describe: searchApi.images,
        codings: searchApi.analyses,
        manual: searchApi.manual,
      })[mode],
    [mode],
  );
  const search = useSearch(runner);

  // A new mode asks a different question, so the previous answer is not an answer
  // to it. Clearing beats leaving stale results under a changed query box.
  useEffect(() => {
    search.reset();
  }, [mode, search.reset]); // eslint-disable-line react-hooks/exhaustive-deps

  // An uploaded file becomes the query image. The upload hook caps at one, so the
  // newest file simply replaces whatever was there.
  useEffect(() => {
    const item = upload.items[0];
    if (item && item.id !== seed?.image_id) {
      setSeed({ image_id: item.id, filename: item.filename, previewUrl: item.previewUrl });
    }
  }, [upload.items, seed?.image_id]);

  const active = MODES.find((entry) => entry.id === mode);
  const indexed = status.rows(active.collection);

  const run = useCallback(() => {
    if (mode === 'similar') {
      if (!seed?.image_id) {
        toast.warn('No query image', 'Choose or drop an image to find others like it.');
        return;
      }
      search.search({ image_id: seed.image_id, limit });
      return;
    }
    const query = text.trim();
    if (!query) {
      toast.warn('Nothing to search for', 'Describe what you are looking for.');
      return;
    }
    if (mode === 'describe') search.search({ query, limit });
    if (mode === 'codings') {
      search.search({
        query,
        limit,
        ...(versionId ? { version_id: versionId } : {}),
        ...(answerMode ? { mode: answerMode } : {}),
      });
    }
    if (mode === 'manual') {
      search.search({ query, limit, ...(attribute ? { attribute } : {}) });
    }
  }, [mode, seed, text, limit, versionId, answerMode, attribute, search, toast]);

  /** Clicking a result tile pivots the search onto that image. */
  const pivot = useCallback(
    (hit) => {
      setSeed({ image_id: hit.image_id, filename: hit.filename });
      setMode('similar');
      search.search({ image_id: hit.image_id, limit });
    },
    [limit, search],
  );

  return (
    <AppShell
      sidebar={
        <Sidebar
          conversations={conversations.conversations}
          loading={conversations.loading}
          onNewAnalysis={() => navigate('/')}
          onSelectConversation={(id) => navigate(`/c/${id}`)}
          onDeleteConversation={conversations.remove}
          onPinConversation={conversations.setPinned}
        />
      }
    >
      <main className="page">
        <TopBar title="Search" />
        <div className="page__body">
          <div className="page__head">
            <div className="page__subtitle">
              Find survey images that look alike, and codings that read alike. This searches by
              meaning rather than by value — the questions a <code>WHERE</code> clause cannot
              answer. It reads the same PostgreSQL records the rest of the app writes; nothing here
              is a separate source of truth.
            </div>
          </div>

          {status.loading ? (
            <Loading label="Checking whether search is available…" />
          ) : (
            <>
              <section className="card">
                <Tabs
                  tabs={MODES.map((entry) => ({
                    id: entry.id,
                    label: entry.label,
                    count: status.available
                      ? status.rows(entry.collection) || undefined
                      : undefined,
                  }))}
                  active={mode}
                  onChange={setMode}
                />
                <div className="card--pad">
                  {!status.available ? (
                    <Unavailable status={status} />
                  ) : (
                    <QueryRow
                      mode={mode}
                      text={text}
                      setText={setText}
                      limit={limit}
                      setLimit={setLimit}
                      versionId={versionId}
                      setVersionId={setVersionId}
                      answerMode={answerMode}
                      setAnswerMode={setAnswerMode}
                      attribute={attribute}
                      setAttribute={setAttribute}
                      seed={seed}
                      clearSeed={() => {
                        setSeed(null);
                        upload.clear();
                      }}
                      onAddFiles={upload.addFiles}
                      uploading={upload.uploading}
                      dropping={dropping}
                      setDropping={setDropping}
                      versions={readyVersions}
                      searching={search.searching}
                      onRun={run}
                    />
                  )}
                </div>
              </section>

              {status.available && (
                <Results
                  mode={mode}
                  search={search}
                  indexed={indexed}
                  collection={active.collection}
                  onPick={pivot}
                  activeId={seed?.image_id || null}
                />
              )}
            </>
          )}
        </div>
      </main>
    </AppShell>
  );
}

/**
 * Why search cannot answer, and what to do about it.
 *
 * Shown in place of the query controls rather than beside them: a search box that
 * cannot search is worse than no search box, because it invites someone to type a
 * query and read the failure as being about their words.
 */
function Unavailable({ status }) {
  return (
    <EmptyState
      glyph="◎"
      title="Similarity search is not available"
      action={
        <Button variant="ghost" size="sm" onClick={status.refresh} style={{ marginTop: 12 }}>
          Check again
        </Button>
      }
    >
      {status.reason()}
      <br />
      Everything else — coding images, batch scoring, comparing versions — is unaffected. See{' '}
      <code>docs/MILVUS.md</code> §0 for the setup runbook.
    </EmptyState>
  );
}

function QueryRow({
  mode,
  text,
  setText,
  limit,
  setLimit,
  versionId,
  setVersionId,
  answerMode,
  setAnswerMode,
  attribute,
  setAttribute,
  seed,
  clearSeed,
  onAddFiles,
  uploading,
  dropping,
  setDropping,
  versions,
  searching,
  onRun,
}) {
  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onRun();
    }
  };

  return (
    <>
      {mode === 'similar' ? (
        <div className="squery">
          {seed ? (
            <div className="sseed">
              <img
                src={seed.previewUrl || imagesApi.fileUrl(seed.image_id, { thumb: true })}
                alt={seed.filename || 'Query image'}
              />
              <div className="sseed__meta">
                <div className="sseed__name">{seed.filename || 'Query image'}</div>
                <div className="sseed__id">{seed.image_id}</div>
              </div>
            </div>
          ) : (
            <div
              className={`drop ${dropping ? 'drop--active' : ''}`}
              style={{ flex: 1 }}
              onDragOver={(event) => {
                event.preventDefault();
                setDropping(true);
              }}
              onDragLeave={() => setDropping(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDropping(false);
                onAddFiles(event.dataTransfer.files);
              }}
            >
              <div className="drop__title">Drop an image here</div>
              <div className="drop__hint">
                Or click a result below to search from it instead. The query image is excluded from
                its own results.
              </div>
              <label className="btn btn--sm" style={{ marginTop: 12 }}>
                Choose file
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={(event) => {
                    onAddFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
              </label>
              {uploading && <Loading label="Uploading…" />}
            </div>
          )}
          {seed && (
            <>
              <Button variant="ghost" onClick={clearSeed}>
                Change image
              </Button>
              <Button onClick={onRun} disabled={searching}>
                {searching ? 'Searching…' : 'Find similar'}
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="squery">
          <div className="squery__input">
            <TextInput
              label={LABELS[mode].label}
              placeholder={LABELS[mode].placeholder}
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={onKeyDown}
              hint={mode === 'describe' ? tokenHint(text) : LABELS[mode].hint}
            />
          </div>
          <Button onClick={onRun} disabled={searching} style={{ marginTop: 22 }}>
            {searching ? 'Searching…' : 'Search'}
          </Button>
        </div>
      )}

      <div className="sfilters">
        {mode === 'codings' && (
          <>
            <Select
              label="VERSION"
              value={versionId}
              placeholder="Any version"
              options={versions.map((version) => ({
                value: version.id,
                label: version.label || version.id,
              }))}
              onChange={(event) => setVersionId(event.target.value)}
            />
            <Select
              label="ANSWER TYPE"
              value={answerMode}
              placeholder="Any"
              options={[
                { value: 'extraction', label: 'Attribute extraction' },
                { value: 'chat', label: 'Free-text answer' },
              ]}
              onChange={(event) => setAnswerMode(event.target.value)}
            />
          </>
        )}
        {mode === 'manual' && (
          <TextInput
            label="ATTRIBUTE"
            placeholder="Any attribute"
            value={attribute}
            onChange={(event) => setAttribute(event.target.value)}
            onKeyDown={onKeyDown}
            hint="Exact name, e.g. Median Type"
          />
        )}
        <div className="field--narrow">
          <TextInput
            label="RESULTS"
            type="number"
            min={1}
            max={200}
            value={limit}
            onChange={(event) => setLimit(Number(event.target.value) || 20)}
          />
        </div>
      </div>
    </>
  );
}

const LABELS = {
  describe: {
    label: 'DESCRIBE THE SCENE',
    placeholder: 'metal safety barrier on the driver side',
    hint: '',
  },
  codings: {
    label: 'SEARCH THE CODINGS',
    placeholder: 'physical median with no barrier',
    hint: 'Searches the answer text and its parsed attribute values together.',
  },
  manual: {
    label: 'SEARCH THE GUIDANCE',
    placeholder: 'when is a median considered physical',
    hint: 'Only guidance you have ingested yourself.',
  },
};

/**
 * A live token estimate, because the truncation it warns about is silent.
 *
 * Words times 1.3 is a rough stand-in for the real tokeniser, which runs on the
 * worker. It does not need to be exact — it needs to tell someone writing a
 * paragraph that the tail of it will be thrown away.
 */
function tokenHint(text) {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const estimate = Math.ceil(words * 1.3);
  if (!words) {
    return `A phrase, not a paragraph — the image model's text side caps at ${TEXT_TOWER_TOKENS} tokens.`;
  }
  if (estimate > TEXT_TOWER_TOKENS) {
    return `About ${estimate} tokens — past ${TEXT_TOWER_TOKENS} the rest is silently dropped. Shorten it.`;
  }
  return `About ${estimate} of ${TEXT_TOWER_TOKENS} tokens.`;
}

/**
 * Results, or the right explanation for their absence.
 *
 * The order of these branches is the whole point. An unreachable store is checked
 * before a failed query, and a failed query before an empty one, because each
 * earlier cause makes the later ones look true. Collapsing them into one "no
 * results" message would send someone to rewrite a query when the real answer is
 * that the store is not answering at all.
 *
 * The row count deliberately does *not* gate any of this — see the comment on the
 * pre-query branch. It explains an empty screen; it never causes one.
 */
function Results({ mode, search, indexed, collection, onPick, activeId }) {
  if (search.searching && !search.results) {
    return (
      <section className="card card--pad">
        <div className="sskel">
          <div className="sskel__row" />
          <div className="sskel__row" />
          <div className="sskel__row" />
          <div className="sskel__row" />
        </div>
      </section>
    );
  }

  if (search.unavailable) {
    return (
      <section className="card card--pad">
        <ErrorState error={search.error} title="Search could not answer" />
      </section>
    );
  }

  if (search.error) {
    return (
      <section className="card card--pad">
        <ErrorState error={search.error} />
      </section>
    );
  }

  // Before a query, and *never* as a gate on searching.
  //
  // This branch used to come first and short-circuit on `indexed === 0`, which
  // made the row count load-bearing — and it is not trustworthy enough for that.
  // Milvus reports counts from sealed segments, so freshly indexed rows read as
  // zero for as long as it takes a flush to happen. The page therefore told
  // someone with a fully populated store that nothing was indexed, and pointed
  // them at a backfill they had already run.
  //
  // The count is now advisory: it colours the message, it never withholds the
  // search box. A stale statistic can mislabel an empty screen; it can no longer
  // prevent you from proving it wrong.
  if (!search.ran) {
    return (
      <section className="card card--pad">
        <EmptyState glyph="◎" title="Nothing searched yet">
          {indexed > 0 ? (
            <>
              {indexed.toLocaleString()} record{indexed === 1 ? '' : 's'} indexed and ready.
            </>
          ) : (
            <>
              The index reports no rows — though Milvus counts only flushed segments, so recent ones
              may not be counted yet. Run a search and see.
            </>
          )}
        </EmptyState>
      </section>
    );
  }

  if (search.results.length === 0) {
    // Zero results AND zero rows is the point at which "nothing is indexed" is a
    // safe thing to say: the search itself has now confirmed it.
    return (
      <section className="card card--pad">
        {indexed === 0 ? (
          <EmptyState glyph="◎" title="Nothing indexed yet">
            {collection === 'manual_chunks' ? (
              <>
                Coding guidance has no relational source — it only exists once you ingest it through{' '}
                <code>POST /search/manual/ingest</code>. Nothing ships with the application.
              </>
            ) : (
              <>
                Indexing is not retroactive, so anything created before search was working is
                invisible to it. Backfill with <code>python scripts/reindex_vectors.py --all</code>.
              </>
            )}
          </EmptyState>
        ) : (
          <EmptyState glyph="◎" title="No matches">
            Searched {indexed.toLocaleString()} record{indexed === 1 ? '' : 's'}.{' '}
            {mode === 'describe'
              ? 'Try a shorter, more concrete phrase — or search from an image instead.'
              : 'Try different words, or widen the filters.'}
          </EmptyState>
        )}
      </section>
    );
  }

  return (
    <section className="card card--pad">
      {collection === 'survey_images' && (
        <ImageResults
          results={search.results}
          meta={search.meta}
          onPick={onPick}
          activeId={activeId}
        />
      )}
      {collection === 'analyses' && <AnalysisResults results={search.results} meta={search.meta} />}
      {collection === 'manual_chunks' && (
        <ManualResults results={search.results} meta={search.meta} />
      )}
    </section>
  );
}
